"use client";
import { useEffect, useRef, useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useIntersectionObserver } from '@uidotdev/usehooks';
import Image from 'next/image';
import Link from 'next/link';
import Footer from '@/components/Footer';
import Newscard from '@/components/NewsCard';
import HomeNavHeader from '@/components/HomeNavHeader';
import Eventcard from '@/components/EventCard';
import GalleryCard from '@/components/GalleryCard';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [subtextHeight, setSubtextHeight] = useState(0);
  const { user } = useClerk();
  const subtextRef = useRef<HTMLParagraphElement>(null);
  
  
  const [principleParentRef, principleParentEntry] = useIntersectionObserver({ 
    threshold: 0.2,
    rootMargin: '0px 0px -50px 0px'
  });
  const [hasAnimatedPrinciple, setHasAnimatedPrinciple] = useState(false);

  
  useEffect(() => {
    if (subtextRef.current) {
      setSubtextHeight(subtextRef.current.offsetHeight);
    }
  }, []);

  
  useEffect(() => {
    if (principleParentEntry?.isIntersecting && !hasAnimatedPrinciple) {
      setHasAnimatedPrinciple(true);
    }
  }, [principleParentEntry, hasAnimatedPrinciple]);

  

  return (
    <main className='overflow-x-hidden' style={{ backgroundColor: '#f8f9fa' }}>
      {/* Header Section */}
      <div className='flex items-center h-[50px] md:h-[70px] lg:h-[100px] lg:-mx-6 md:-mt-[5px] lg:-mt-3 pt-3 mb-3 md:mb-2 lg:mb-0 pr-2 lg:pr-0'>
        <HomeNavHeader/>
      </div>
      
      {/* Overview Section */}
      <section className="relative md:min-h-[60vh] lg:min-h-[90vh] bg-[linear-gradient(rgba(4,9,30,0.7),rgba(4,9,30,0.7)),url(https://res.cloudinary.com/swsumuahia/image/upload/q_auto/f_auto/v1776887473/banner_pcu5ey.jpg)] bg-cover bg-center flex items-center p-8 lg:p-16 mb-8">
        <div className="text-white max-w-full">
          <h1 className="text-[30px] md:text-4xl lg:text-5xl font-bold mb-8 animate-slide-in-left">
              Welcome to Seat of Wisdom Seminary, <br/>Umuahia
          </h1>
          <div className="flex items-start gap-4">
              <div 
                  className="w-0.5 bg-red-600" 
                  style={{ height: `${subtextHeight}px` }}
              />
              <p 
                  ref={subtextRef}
                  className=" text-sm md:text-lg lg:text-lg max-w-3xl animate-slide-in text-white font-light text-justify"
              >
                  Our mission is to form spiritually grounded, intellectually sharp, and pastorally compassionate priests who carry forward Christ’s mission by embodying the Church’s living presence in the world.
              </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="flex flex-col md:flex-row md:h-[260px] lg:h-[300px] md:pr-6">
        <div 
          ref={principleParentRef}
          className={`bg-[linear-gradient(rgba(4,9,30,0.7),rgba(4,9,30,0.7)),url(/assets/images/footer_image.png)] lg:w-[70vw] ${
            hasAnimatedPrinciple 
              ? 'animate-slide-in-left opacity-100' 
              : 'opacity-0'
          } transition-opacity duration-300`}
        >
          <InfoBox 
            title="Our Principle" 
            bg="text-white"
          >
            We uphold integrity, excellence, and innovation as our guiding principles...
          </InfoBox>
        </div>

        <InfoBox2 
            title="Our Philosophy" 
            bg="bg-gray-200"
            animation="animate-slide-in-right"
          >
            We believe that education is a transformative force...
          </InfoBox2>
          
          <InfoBox2 
            title="Key to Success" 
            bg="bg-gray-100"
            animation="animate-slide-in-right"
          >
            Success is built on holistic formation...
          </InfoBox2>
      </section>
      
      <div>
        {/* Events Section */}
        <div className="even:bg-slate-100 md:px-2 lg:px-2 mb-6 mt-2 py-3">
        <div className="flex items-center justify-center">
          <Link href='/events'>
            <h2 className="font-bold text-[24px] pt-2">Upcoming Events</h2>
          </Link>
        </div>
          <Eventcard />
        </div>

        {/* News Section */}
        <div className="even:bg-slate-100 md:px-12 lg:px-24 py-3 mb-6 mt-2">
          <div className="flex items-center justify-center">
            <Link href="/news_archive">
              <h2 className="font-bold text-[24px] pt-4">Latest News</h2>
            </Link>
          </div>
          <Newscard />
        </div>

        {/* Gallery Section */}
        <div className="even:bg-slate-100 md:px-12 lg:px-24 py-3 mb-6 mt-2">
          <div className="flex items-center justify-center">
            <Link href="/gallery">
              <h2 className="font-bold text-[24px]">Gallery</h2>
            </Link>
          </div>
          <GalleryCard/>
        </div>
      </div>

      {/* Footer Section */}
      <Footer/>
    </main>
  );
}


const InfoBox = ({ title, bg, children, animation }: any) => {
  const [ref, entry] = useIntersectionObserver({ 
    threshold: 0.2,
    rootMargin: '0px 0px -50px 0px'
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  
  
  useEffect(() => {
    if (entry?.isIntersecting && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [entry, hasAnimated]);

  return (
    <div 
      ref={ref}
      className={`p-8 ${bg} ${
        hasAnimated ? 'opacity-100' : 'opacity-0'
      } ${hasAnimated ? animation : ''}`}
    >
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <span className="w-0.5 h-5 bg-red-600 inline-block" />
        {title}
      </h3>
      <p className="text-white text-[14px] md:text-[16px]">{children}</p>
    </div>
  );
};

const InfoBox2 = ({ title, bg, children, animation }: any) => {
  const [ref, entry] = useIntersectionObserver({ 
    threshold: 0.2,
    rootMargin: '0px 0px -50px 0px'
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  
  
  useEffect(() => {
    if (entry?.isIntersecting && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [entry, hasAnimated]);

  return (
    <div 
      ref={ref}
      className={`p-8 ${bg} ${
        hasAnimated ? 'opacity-100' : 'opacity-0'
      } ${hasAnimated ? animation : ''}`}
    >
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <span className="w-0.5 h-5 bg-red-600 inline-block" />
        {title}
      </h3>
      <p className="text-gray-600 text-[14px] md:text-[16px]">{children}</p>
    </div>
  );
};